/**
 * datevCp1252 — Windows-1252-(CP1252)-Byte-Encoder für den DATEV-Export (M7).
 *
 * Reine Funktionen, voll unit-testbar. Dies ist die einzige FE-spezifische Mechanik
 * gegenüber PHP-`iconv`: JS-`TextEncoder` kann NUR UTF-8, DATEV erwartet aber CP1252.
 *
 * Auflagen (solar-m7-datev-ruling.md):
 *   A2 — Der finale Blob MUSS aus dem rohen `Uint8Array` gebaut werden
 *        (`new Blob([uint8Array])`), NIEMALS aus dem String (`new Blob([csvString])`):
 *        Letzteres erzwingt UTF-8 und zerschießt alle Umlaute (Mojibake).
 *   A3 — CP1252-Byte-Werte sind verbindlich: ä=0xE4 ö=0xF6 ü=0xFC Ä=0xC4 Ö=0xD6
 *        Ü=0xDC ß=0xDF; insbesondere €=0x80 (existiert NUR in CP1252 an 0x80,
 *        NICHT in ISO-8859-1/Latin-1 → daher zwingend CP1252).
 *   A4 — KEIN BOM.
 *
 * Nicht abbildbare Zeichen → '?' (0x3F), ohne Throw (ein exotischer Name darf den
 * Export nicht sprengen).
 *
 * @module datevCp1252
 */

/**
 * Reverse-Map für den CP1252-Sonderblock 0x80–0x9F:
 * Unicode-Codepoint → CP1252-Byte. Außerhalb dieses Blocks gilt die Latin-1-Identität
 * (0x00–0x7F und 0xA0–0xFF werden direkt auf ihren Codepoint abgebildet).
 */
const CP1252_SPECIALS: ReadonlyMap<number, number> = new Map<number, number>([
  [0x20ac, 0x80], // € EURO SIGN
  [0x201a, 0x82], // ‚ SINGLE LOW-9 QUOTATION MARK
  [0x0192, 0x83], // ƒ LATIN SMALL LETTER F WITH HOOK
  [0x201e, 0x84], // „ DOUBLE LOW-9 QUOTATION MARK
  [0x2026, 0x85], // … HORIZONTAL ELLIPSIS
  [0x2020, 0x86], // † DAGGER
  [0x2021, 0x87], // ‡ DOUBLE DAGGER
  [0x02c6, 0x88], // ˆ MODIFIER LETTER CIRCUMFLEX ACCENT
  [0x2030, 0x89], // ‰ PER MILLE SIGN
  [0x0160, 0x8a], // Š LATIN CAPITAL LETTER S WITH CARON
  [0x2039, 0x8b], // ‹ SINGLE LEFT-POINTING ANGLE QUOTATION MARK
  [0x0152, 0x8c], // Œ LATIN CAPITAL LIGATURE OE
  [0x017d, 0x8e], // Ž LATIN CAPITAL LETTER Z WITH CARON
  [0x2018, 0x91], // ' LEFT SINGLE QUOTATION MARK
  [0x2019, 0x92], // ' RIGHT SINGLE QUOTATION MARK
  [0x201c, 0x93], // " LEFT DOUBLE QUOTATION MARK
  [0x201d, 0x94], // " RIGHT DOUBLE QUOTATION MARK
  [0x2022, 0x95], // • BULLET
  [0x2013, 0x96], // – EN DASH
  [0x2014, 0x97], // — EM DASH
  [0x02dc, 0x98], // ˜ SMALL TILDE
  [0x2122, 0x99], // ™ TRADE MARK SIGN
  [0x0161, 0x9a], // š LATIN SMALL LETTER S WITH CARON
  [0x203a, 0x9b], // › SINGLE RIGHT-POINTING ANGLE QUOTATION MARK
  [0x0153, 0x9c], // œ LATIN SMALL LIGATURE OE
  [0x017e, 0x9e], // ž LATIN SMALL LETTER Z WITH CARON
  [0x0178, 0x9f], // Ÿ LATIN CAPITAL LETTER Y WITH DIAERESIS
]);

/** Ersatz-Byte für nicht abbildbare Zeichen: '?' (ASCII 0x3F). */
const REPLACEMENT_BYTE = 0x3f;

/**
 * Wandelt einen UTF-16-JS-String in ein CP1252-Byte-Array (Uint8Array).
 *
 * Niemals throw — nicht abbildbare Zeichen werden durch '?' (0x3F) ersetzt.
 *
 * @param text - Beliebiger JS-String.
 * @returns CP1252-kodierte Bytes (ohne BOM).
 *
 * @example
 * encodeCp1252('ä')  // Uint8Array([0xE4])
 * encodeCp1252('€')  // Uint8Array([0x80])
 */
export function encodeCp1252(text: string): Uint8Array {
  const bytes: number[] = [];

  for (let i = 0; i < text.length; i += 1) {
    const cp = text.charCodeAt(i);

    if (cp <= 0x7f) {
      // ASCII-Durchgang (inkl. CR 0x0D, LF 0x0A, ; " etc.).
      bytes.push(cp);
      continue;
    }
    if (cp >= 0xa0 && cp <= 0xff) {
      // Latin-1-Identität (Umlaute ä/ö/ü/Ä/Ö/Ü/ß liegen hier).
      bytes.push(cp);
      continue;
    }

    const special = CP1252_SPECIALS.get(cp);
    if (special !== undefined) {
      bytes.push(special);
      continue;
    }

    // 0x80–0x9F ohne Mapping, 0x81/0x8D/0x8F/0x90/0x9D (undefiniert) oder > 0xFF.
    bytes.push(REPLACEMENT_BYTE);
  }

  return Uint8Array.from(bytes);
}

/**
 * Baut den finalen Download-Blob für den DATEV-Buchungsstapel.
 *
 * Verbindet die Zeilen mit CRLF (`\r\n`, inkl. abschließendem CRLF), kodiert das
 * Ergebnis als CP1252 und verpackt die ROHEN Bytes (A2) in einen Blob.
 *
 * KEIN `new Blob([string])` (UTF-8-Falle), KEIN BOM (A4).
 *
 * @param csvLines - Header + Spaltenzeile + Datenzeilen (UTF-16-Strings, noch nicht kodiert).
 * @returns Blob mit CP1252-Bytes (MIME `text/csv`).
 */
export function buildDatevBlob(csvLines: string[]): Blob {
  const joined = csvLines.join('\r\n') + '\r\n';
  const bytes = encodeCp1252(joined);
  // A2: Blob aus rohem Uint8Array — NIEMALS aus dem String.
  return new Blob([bytes], { type: 'text/csv;charset=windows-1252' });
}
